import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, Star, ChevronRight, Anchor, Utensils, Waves, Menu, X, User, Play, Pause, Volume2, VolumeX, Shield, Music, Heart, Sun } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../App';
import { api } from '../App';

// Hero videos for rotation
const heroVideos = [
  {
    type: 'video',
    src: '/assets/videos/hero-village.mp4',
    poster: 'https://images.unsplash.com/photo-1754272900570-09847f6bbeb0?w=1920&q=80'
  }
];

// Gallery videos/images for attractions section
const galleryMedia = [
  { type: 'image', src: 'https://images.unsplash.com/photo-1559128010-7c1ad6e1b6a5?w=800', title: 'Crystal Waters' },
  { type: 'image', src: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=600', title: 'White Sand Beach' },
  { type: 'image', src: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600', title: 'Island Cuisine' },
  { type: 'image', src: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=600', title: 'Snorkeling Adventure' },
  { type: 'image', src: 'https://images.unsplash.com/photo-1682687982501-1e58ab814714?w=600', title: 'Ocean Life' },
  { type: 'image', src: 'https://images.unsplash.com/photo-1540946485063-a40da27545f8?w=600', title: 'Yacht Life' },
];

const HomePage = () => {
  const { user, logout } = useAuth();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const videoRef = useRef(null);
  const promoVideoRef = useRef(null);
  const [promoPlaying, setPromoPlaying] = useState(false);
  const [promoMuted, setPromoMuted] = useState(true);
  const [scrollY, setScrollY] = useState(0);

  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Rotate hero videos every 12 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentVideoIndex((prev) => (prev + 1) % heroVideos.length);
    }, 12000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchTours = async () => {
      try {
        // Seed data first
        await api.post('/seed').catch(() => {});
        const response = await api.get('/tours?is_featured=true');
        setTours(response.data);
      } catch (error) {
        console.error('Error fetching tours:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTours();
  }, []);

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'boating': return <Anchor className="w-4 h-4" />;
      case 'food': case 'restaurant': return <Utensils className="w-4 h-4" />;
      case 'snorkel': return <Waves className="w-4 h-4" />;
      default: return <Anchor className="w-4 h-4" />;
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      boating: 'Boat Tour',
      food: 'Food Tour',
      snorkel: 'Beach & Snorkel',
      bar: 'Beach Bar',
      restaurant: 'Dining',
      combo: 'Combo Tour'
    };
    return labels[category] || category;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/assets/logo.png" alt="The Village at Athol Island" className="h-10 w-auto" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/tours" className="text-muted-foreground hover:text-primary transition-colors font-medium">
                Tour
              </Link>
              <a href="#experiences" className="text-muted-foreground hover:text-primary transition-colors font-medium">
                Experience
              </a>
              <a href="#about" className="text-muted-foreground hover:text-primary transition-colors font-medium">
                About
              </a>
            </div>

            <div className="hidden md:flex items-center gap-4">
              {user ? (
                <div className="flex items-center gap-4">
                  {(user.role === 'operator' || user.role === 'owner' || user.role === 'admin') && (
                    <Link to={user.role === 'owner' || user.role === 'admin' ? '/owner' : '/operator'}>
                      <Button variant="outline" size="sm" data-testid="dashboard-btn">Dashboard</Button>
                    </Link>
                  )}
                  <Link to="/my-bookings">
                    <Button variant="ghost" size="sm" data-testid="my-bookings-btn">My Bookings</Button>
                  </Link>
                  <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn">
                    Logout
                  </Button>
                </div>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" data-testid="login-btn">Login</Button>
                  </Link>
                  <Link to="/register">
                    <Button className="btn-primary" data-testid="register-btn">Sign Up</Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-border">
            <div className="px-4 py-4 space-y-4">
              <Link to="/tours" className="block py-2 text-foreground font-medium" onClick={() => setMobileMenuOpen(false)}>
                Tour
              </Link>
              {user ? (
                <>
                  {(user.role === 'operator' || user.role === 'owner' || user.role === 'admin') && (
                    <Link 
                      to={user.role === 'owner' || user.role === 'admin' ? '/owner' : '/operator'} 
                      className="block py-2 text-foreground font-medium"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Dashboard
                    </Link>
                  )}
                  <Link to="/my-bookings" className="block py-2 text-foreground font-medium" onClick={() => setMobileMenuOpen(false)}>
                    My Bookings
                  </Link>
                  <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="block py-2 text-foreground font-medium">
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="block py-2 text-foreground font-medium" onClick={() => setMobileMenuOpen(false)}>
                    Login
                  </Link>
                  <Link to="/register" className="block py-2 text-foreground font-medium" onClick={() => setMobileMenuOpen(false)}>
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section with Video Background */}
      <section className="relative h-[100vh] min-h-[700px] flex items-center justify-center overflow-hidden">
        {/* Video Background with Crossfade */}
        {heroVideos.map((media, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1500 ease-in-out ${
              currentVideoIndex === index ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <video
              ref={currentVideoIndex === index ? videoRef : null}
              autoPlay
              muted={isMuted}
              loop
              playsInline
              poster={media.poster}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ transform: `translateY(${scrollY * 0.3}px)` }}
            >
              <source src={media.src} type="video/mp4" />
            </video>
          </div>
        ))}
        
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />
        
        {/* Animated particles/shimmer effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white/30 rounded-full animate-pulse" style={{ animationDelay: '0s' }} />
          <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-white/40 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
          <div className="absolute bottom-1/3 left-1/2 w-1.5 h-1.5 bg-white/25 rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 right-1/4 w-1 h-1 bg-white/35 rounded-full animate-pulse" style={{ animationDelay: '1.5s' }} />
        </div>
        
        {/* Video Controls */}
        <div className="absolute bottom-36 right-8 z-20 flex gap-2">
          <button
            onClick={() => setIsMuted(!isMuted)}
            className="p-3 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-all"
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? <VolumeX className="w-5 h-5 text-white" /> : <Volume2 className="w-5 h-5 text-white" />}
          </button>
        </div>
        
        {/* Video Indicators */}
        <div className="absolute bottom-36 left-1/2 transform -translate-x-1/2 flex gap-2 z-20">
          {heroVideos.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentVideoIndex(index)}
              className={`h-1 rounded-full transition-all duration-500 ${
                currentVideoIndex === index 
                  ? 'bg-white w-8' 
                  : 'bg-white/50 w-4 hover:bg-white/75'
              }`}
              aria-label={`Go to video ${index + 1}`}
            />
          ))}
        </div>
        
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <p className="text-[#00bcd4] font-medium tracking-[0.3em] uppercase mb-4 animate-fade-in text-sm">
            The Village at Athol Island
          </p>
          
          {/* Animated 3D-style Title */}
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight">
            <span className="inline-block animate-fade-in stagger-1 drop-shadow-[0_4px_8px_rgba(0,0,0,0.3)]">
              The Heart of the Bahamas
            </span>
            <br />
            <span 
              className="inline-block animate-fade-in stagger-2 bg-gradient-to-r from-[#00bcd4] via-[#ffeb3b] to-[#ff6b6b] bg-clip-text text-transparent drop-shadow-lg"
              style={{ 
                textShadow: '0 0 40px rgba(0, 188, 212, 0.5)',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
              }}
            >
              Lives in The Village.
            </span>
          </h1>
          
          <p className="text-xl sm:text-2xl text-white/90 mb-10 animate-fade-in stagger-3 max-w-2xl mx-auto leading-relaxed font-light">
            A one-of-a-kind island experience on historic Athol Island.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in stagger-4">
            <Link to="/tours">
              <Button 
                className="group bg-[#00bcd4] hover:bg-[#00acc1] text-white text-lg px-10 py-6 rounded-full shadow-lg shadow-[#00bcd4]/30 hover:shadow-xl hover:shadow-[#00bcd4]/40 transition-all duration-300 hover:scale-105"
                data-testid="explore-tours-btn"
              >
                Explore Experiences
                <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <a href="#the-story">
              <Button 
                variant="outline" 
                className="group bg-white/10 border-2 border-white/40 text-white hover:bg-white/20 hover:border-white/60 rounded-full px-10 py-6 text-lg backdrop-blur-sm transition-all duration-300"
              >
                <Play className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                Watch Video
              </Button>
            </a>
          </div>
        </div>

        {/* Trust badges */}
        <div className="absolute bottom-8 left-0 right-0 px-4">
          <div className="glass rounded-2xl max-w-3xl mx-auto p-4">
            <div className="flex flex-wrap justify-center gap-6 text-sm text-foreground">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-primary rounded-full"></span>
                Secure Checkout
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-primary rounded-full"></span>
                Booking Confirmation
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-primary rounded-full"></span>
                Digital Waiver
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-primary rounded-full"></span>
                QR Check-in
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ THE STORY — Introduction to Athol Island ═══ */}
      <section id="the-story" className="py-20 md:py-32 px-4 md:px-8 bg-white" data-testid="story-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img src="https://images.unsplash.com/photo-1754272900570-09847f6bbeb0?w=800&q=80" alt="Athol Island turquoise beach" className="w-full h-[400px] md:h-[500px] object-cover" />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#00bcd4] text-white p-5 rounded-2xl shadow-xl hidden md:block">
                <p className="text-3xl font-serif font-bold">15</p>
                <p className="text-sm font-medium opacity-90">minutes from Nassau</p>
              </div>
            </div>
            <div>
              <p className="text-[#00bcd4] font-medium tracking-[0.2em] uppercase mb-3 text-sm">Our Story</p>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 leading-tight">The Village at<br/>Athol Island</h2>
              <div className="space-y-5 text-muted-foreground text-base md:text-lg leading-relaxed">
                <p>Welcome to <strong className="text-foreground">The Village at Athol Island</strong>, a one-of-a-kind island experience on historic Athol Island.</p>
                <p>Just off the coast of New Providence and less than 15 minutes by boat from Nassau, The Village is close enough for convenience, yet far enough to feel like paradise. Escape the bustle of the city and discover a place where Bahamian history, culture, and natural beauty come together.</p>
              </div>
              <Link to="/tours" className="inline-flex items-center gap-2 text-[#00bcd4] font-medium hover:underline mt-4">
                Discover Our Story <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ THE SENSES — Poetic sensory experience ═══ */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-testid="senses-section">
        <div className="absolute inset-0 bg-cover bg-center bg-fixed" style={{ backgroundImage: 'url(https://images.pexels.com/photos/913100/pexels-photo-913100.jpeg?auto=compress&cs=tinysrgb&w=1920)' }} />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 text-center">
          <div className="space-y-6 md:space-y-8">
            {['Step onto an island rich in Bahamian history, where the shoreline tells a story waiting to be discovered.', 'Long cherished as one of The Bahamas\' best-kept secrets, Athol Island invites you to experience the culture, beauty, and spirit of the islands.'].map((line, i) => (
              <p key={i} className="font-serif text-2xl sm:text-3xl md:text-4xl text-white font-light italic leading-snug" style={{ opacity: 0.85 + (i * 0.05) }}>{line}</p>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ THE CULTURE — Junkanoo & Rake n Scrape ═══ */}
      <section className="py-20 md:py-32 px-4 md:px-8 bg-[#faf9f6]" data-testid="culture-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1">
              <p className="text-[#ff6b6b] font-medium tracking-[0.2em] uppercase mb-3 text-sm">Heritage & Rhythm</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-6 leading-tight">The Heart of The Bahamas<br/>Comes Alive</h2>
              <div className="space-y-5 text-muted-foreground text-base md:text-lg leading-relaxed">
                <p>The Village at Athol Island brings the history and heritage of The Bahamas to life through music, color, storytelling, and authentic island traditions.</p>
                <p>Feel the rhythm of <strong className="text-foreground">Junkanoo</strong> as the goatskin drums set the beat and the energy comes alive around you. Move to the sounds of <strong className="text-foreground">rake-and-scrape</strong> and timeless Bahamian classics, music rooted in folklore, celebration, and generations of island tradition.</p>
                <p className="text-foreground font-medium italic border-l-4 border-[#ff6b6b] pl-5">What truly makes The Village at Athol Island unforgettable isn't just the breathtaking scenery; it's the people, the culture, and the spirit of The Bahamas.</p>
              </div>
            </div>
            <div className="order-1 lg:order-2 grid grid-cols-2 gap-3">
              <div className="rounded-2xl overflow-hidden aspect-square"><img src="/images/tours/taste-of-bahamas-new/VillageRestaurant&Bar-5.jpg" alt="Junkanoo performers" className="w-full h-full object-cover" /></div>
              <div className="rounded-2xl overflow-hidden aspect-square mt-8"><img src="/images/tours/taste-of-bahamas-new/VillageRestaurant&Bar-8.jpg" alt="Bahamian cultural dance" className="w-full h-full object-cover" /></div>
              <div className="rounded-2xl overflow-hidden aspect-square"><img src="/images/tours/taste-of-bahamas-new/VillageRestaurant&Bar-10.jpg" alt="Island music performance" className="w-full h-full object-cover" /></div>
              <div className="rounded-2xl overflow-hidden aspect-square mt-8"><img src="/images/tours/taste-of-bahamas-new/VillageRestaurant&Bar-20.jpg" alt="Cultural celebration" className="w-full h-full object-cover" /></div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ THE EXPERIENCE — Promo Video ═══ */}
      <section className="relative py-20 md:py-28 overflow-hidden bg-[#0a0a0a]" data-testid="promo-video-section">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <p className="text-[#00bcd4] font-medium tracking-[0.2em] uppercase mb-3 text-sm">See It For Yourself</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">See The Village Come to Life</h2>
            <p className="text-white/70 max-w-2xl mx-auto">Discover the food, music, crystal waters, and Bahamian spirit that make any day at The Village memorable.</p>
          </div>
          
          <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl border border-white/10 group" data-testid="promo-video-player">
            <video
              ref={promoVideoRef}
              muted={promoMuted}
              loop
              playsInline
              preload="metadata"
              className="w-full h-full object-cover"
            >
              <source src="/assets/videos/promo-village.mp4" type="video/mp4" />
            </video>
            
            {/* Large centered play button */}
            <div 
              className="absolute inset-0 flex items-center justify-center cursor-pointer bg-black/30 transition-opacity duration-300"
              style={{ opacity: promoPlaying ? 0 : 1, pointerEvents: promoPlaying ? 'none' : 'auto' }}
              onClick={() => {
                if (promoVideoRef.current) {
                  promoVideoRef.current.play();
                  setPromoPlaying(true);
                }
              }}
              data-testid="promo-play-overlay"
            >
              <div className="w-20 h-20 bg-[#00bcd4] rounded-full flex items-center justify-center shadow-lg shadow-[#00bcd4]/40 hover:scale-110 transition-transform duration-300">
                <Play className="w-8 h-8 text-white ml-1" />
              </div>
            </div>

            {/* Bottom controls - visible on hover when playing */}
            <div className={`absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent transition-opacity duration-300 ${promoPlaying ? 'opacity-0 group-hover:opacity-100' : 'opacity-0'}`}>
              <div className="flex items-center justify-between">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (promoVideoRef.current) {
                      if (promoPlaying) { promoVideoRef.current.pause(); } else { promoVideoRef.current.play(); }
                      setPromoPlaying(!promoPlaying);
                    }
                  }}
                  className="p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-all"
                  data-testid="promo-pause-btn"
                >
                  {promoPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPromoMuted(!promoMuted);
                  }}
                  className="p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-all"
                  data-testid="promo-mute-btn"
                >
                  {promoMuted ? <VolumeX className="w-5 h-5 text-white" /> : <Volume2 className="w-5 h-5 text-white" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ THE TASTE — Food & Activities intro ═══ */}
      <section className="py-20 md:py-28 px-4 md:px-8 bg-white" data-testid="taste-activities-section">
        <div className="max-w-5xl mx-auto text-center mb-16">
          <p className="text-[#00bcd4] font-medium tracking-[0.2em] uppercase mb-3 text-sm">Island Flavors & Island Fun</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-6">Island Flavors. Island Fun.</h2>
          <p className="text-muted-foreground text-base md:text-lg max-w-3xl mx-auto leading-relaxed">Indulge in authentic Bahamian cuisine, freshly prepared, full of flavor, and crafted by those who know the island's recipes best. Whether it's your first taste of Bahamian cooking or one of your favorite island dishes, every meal is made to order using local traditions and hospitality.</p>
          <p className="text-foreground font-medium mt-6 text-base md:text-lg">After your meal, grab a drink from the waterfront bar, cool off in the freshwater pool, or head out for optional water activities like snorkeling and jet skis. Play beach games with family and friends, gather in the plaza, or simply unwind beneath the shade of a swaying palm tree. Whether you're seeking adventure, celebration, or complete relaxation, The Village at Athol Island offers an authentic Bahamian escape for all ages.</p>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          
          {/* Clean 3-column grid with consistent sizing */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {/* First large image - spans 2 columns on mobile, 2 columns and 2 rows on desktop */}
            <div className="col-span-2 md:row-span-2 relative rounded-2xl overflow-hidden group cursor-pointer aspect-[4/3] md:aspect-auto" data-testid="gallery-image-1">
              <img 
                src="/images/tours/taste-of-bahamas-new/VillageRestaurant&Bar-73.jpg" 
                alt="Tropical Cocktails"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-6 left-6">
                  <p className="text-white text-xl font-serif font-bold">Tropical Cocktails</p>
                  <p className="text-white/80 text-sm">Handcrafted island drinks with a Caribbean flair</p>
                </div>
              </div>
            </div>
            
            {/* Conch Salad Wrap */}
            <div className="relative rounded-2xl overflow-hidden group cursor-pointer aspect-square" data-testid="gallery-image-2">
              <img 
                src="/images/tours/taste-of-bahamas-new/VillageRestaurant&Bar-54.jpg" 
                alt="Fresh Conch Salad"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-4 left-4">
                  <p className="text-white font-semibold">Fresh Conch Salad</p>
                </div>
              </div>
            </div>
            
            {/* Fried Chicken Plate */}
            <div className="relative rounded-2xl overflow-hidden group cursor-pointer aspect-square" data-testid="gallery-image-3">
              <img 
                src="/images/tours/taste-of-bahamas-new/VillageRestaurant&Bar-42.jpg" 
                alt="Island Fried Chicken"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-4 left-4">
                  <p className="text-white font-semibold">Island Fried Chicken</p>
                </div>
              </div>
            </div>
            
            {/* Tropical Cocktail */}
            <div className="relative rounded-2xl overflow-hidden group cursor-pointer aspect-square" data-testid="gallery-image-4">
              <img 
                src="/images/tours/taste-of-bahamas-new/VillageRestaurant&Bar-70.jpg" 
                alt="Signature Rum Punch"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-4 left-4">
                  <p className="text-white font-semibold">Signature Rum Punch</p>
                </div>
              </div>
            </div>
            
            {/* Burger & Fries */}
            <div className="relative rounded-2xl overflow-hidden group cursor-pointer aspect-square" data-testid="gallery-image-5">
              <img 
                src="/images/tours/taste-of-bahamas-new/VillageRestaurant&Bar-49.jpg" 
                alt="Bahamian Burger & Fries"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-4 left-4">
                  <p className="text-white font-semibold">Bahamian Burger & Fries</p>
                </div>
              </div>
            </div>
            
            {/* Frozen Drink */}
            <div className="relative rounded-2xl overflow-hidden group cursor-pointer aspect-square" data-testid="gallery-image-6">
              <img 
                src="/images/tours/taste-of-bahamas-new/VillageRestaurant&Bar-71.jpg" 
                alt="Frozen Strawberry Daiquiri"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-4 left-4">
                  <p className="text-white font-semibold">Frozen Strawberry Daiquiri</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Experience Section */}
      <section id="experiences" className="py-16 md:py-24 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#00bcd4] font-medium tracking-wider uppercase mb-2">Handpicked for You</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-4">
              Featured Experience
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Discover the best way to experience Nassau and Paradise Island with our signature tour.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : tours.length > 0 ? (
            <div className="flex justify-center">
              <Link 
                to={`/tours/${tours[0].id}`}
                className="tour-card bg-white rounded-2xl overflow-hidden shadow-lg border border-border/50 animate-fade-in group w-full max-w-3xl"
                data-testid="featured-tour-card"
              >
                {/* Video Header */}
                <div className="relative h-72 sm:h-80 md:h-96 overflow-hidden">
                  <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    poster="https://images.unsplash.com/photo-1552210668-67a72ecb6c08?w=1200&q=80"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    data-testid="featured-tour-video"
                  >
                    <source src="/assets/videos/boat-cruise.mp4" type="video/mp4" />
                  </video>
                  {tours[0].is_popular && (
                    <span className="absolute top-4 left-4 bg-coral text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                      Popular
                    </span>
                  )}
                  <span className="absolute bottom-4 left-4 glass px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1.5">
                    {getCategoryIcon(tours[0].category)}
                    {getCategoryLabel(tours[0].category)}
                  </span>
                </div>
                
                {/* Tour Info */}
                <div className="p-6 md:p-8">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {tours[0].location}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {tours[0].duration_hours}h
                    </span>
                  </div>
                  
                  <h3 className="font-serif font-bold text-2xl md:text-3xl text-foreground mb-3">
                    {tours[0].title}
                  </h3>
                  
                  <p className="text-muted-foreground text-base mb-6">
                    {tours[0].short_description || tours[0].description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-right">
                      <span className="text-sm text-muted-foreground">From</span>
                      <p className="font-bold text-3xl text-primary">BSD ${tours[0].base_price}</p>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          ) : null}

          <div className="text-center mt-12">
            <Link to="/tours">
              <Button variant="outline" className="btn-secondary" data-testid="view-all-tours-btn">
                View Experience
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ THE PROMISE — Guarantees ═══ */}
      <section id="about" className="py-16 md:py-24 px-4 md:px-8 bg-white" data-testid="promise-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#00bcd4] font-medium tracking-[0.2em] uppercase mb-3 text-sm">Our Promise</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-4">
              What You Can Expect
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[
              { icon: Shield, title: 'Safe & Professional Operations', desc: 'Licensed crew, safety briefings, and well-maintained vessels on every crossing.', color: '#00bcd4' },
              { icon: Star, title: 'Warm Bahamian Hospitality', desc: 'A welcome that feels like family, from the dock to the last boat home.', color: '#ffeb3b' },
              { icon: Heart, title: 'Authentic Island Culture', desc: 'Bahamian food, music, and traditions shared the way they are meant to be experienced.', color: '#ff6b6b' },
              { icon: Sun, title: 'A Day to Remember', desc: 'Beaches, flavors, and island moments that stay with you long after you leave.', color: '#4caf50' },
            ].map((item, i) => (
              <div key={i} className="text-center p-6 rounded-2xl bg-[#faf9f6] border border-border/30">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${item.color}15` }}>
                  <item.icon className="w-7 h-7" style={{ color: item.color }} />
                </div>
                <h3 className="font-serif font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Closing Tagline */}
          <div className="relative text-center py-12 px-6 rounded-3xl bg-gradient-to-br from-[#0a1628] to-[#0a0a0a] overflow-hidden" data-testid="closing-tagline">
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #00bcd4 0%, transparent 50%), radial-gradient(circle at 70% 50%, #ffeb3b 0%, transparent 50%)' }} />
            <div className="relative z-10 max-w-2xl mx-auto">
              <p className="font-serif text-2xl md:text-3xl text-white font-light leading-relaxed mb-2">Discover Historic Athol Island.</p>
              <p className="font-serif text-2xl md:text-3xl text-white font-light leading-relaxed mb-2">Experience The Village.</p>
              <p className="font-serif text-2xl md:text-3xl font-medium leading-relaxed bg-gradient-to-r from-[#00bcd4] via-[#ffeb3b] to-[#ff6b6b] bg-clip-text text-transparent">
                Leave inspired by the spirit of The Bahamas.
              </p>
              <div className="mt-8">
                <Link to="/tours">
                  <Button className="bg-[#00bcd4] hover:bg-[#00acc1] text-white px-10 py-5 rounded-full text-lg shadow-lg shadow-[#00bcd4]/30 hover:shadow-xl transition-all duration-300 hover:scale-105" data-testid="closing-cta-btn">
                    Book Your Escape
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0a0a0a] text-white py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div>
              <img src="/assets/logo.png" alt="The Village at Athol Island" className="h-12 w-auto" />
            </div>
              <p className="text-white/70 text-sm mt-4">
                The Heart of the Bahamas Lives in The Village.
              </p>
            </div>

            <div>
              <h4 className="font-bold mb-4">Experiences</h4>
              <ul className="space-y-2 text-white/70 text-sm">
                <li><Link to="/tours" className="hover:text-[#00bcd4] transition-colors">Boat Tour</Link></li>
                <li><Link to="/tours" className="hover:text-[#00bcd4] transition-colors">Food Tour</Link></li>
                <li><Link to="/tours" className="hover:text-[#00bcd4] transition-colors">Beach & Snorkel</Link></li>
                <li><Link to="/tours" className="hover:text-[#00bcd4] transition-colors">Private Charter</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4">Support</h4>
              <ul className="space-y-2 text-white/70 text-sm">
                <li><Link to="/faq" className="hover:text-[#00bcd4] transition-colors">FAQs</Link></li>
                <li><Link to="/cancellation-policy" className="hover:text-[#00bcd4] transition-colors">Cancellation Policy</Link></li>
                <li><Link to="/safety-guidelines" className="hover:text-[#00bcd4] transition-colors">Safety Guidelines</Link></li>
                <li><Link to="/privacy-policy" className="hover:text-[#00bcd4] transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms-conditions" className="hover:text-[#00bcd4] transition-colors">Terms &amp; Conditions</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <ul className="space-y-2 text-white/70 text-sm">
                <li>Athol Island, Nassau, The Bahamas</li>
                <li><a href="tel:+12428256265" className="hover:text-[#00bcd4] transition-colors">+1 (242) 825-6265</a></li>
                <li><a href="mailto:hello@thevillageatholisland.com" className="hover:text-[#00bcd4] transition-colors">hello@thevillageatholisland.com</a></li>
                <li>Daily, 9:00 AM - 5:00 PM (EST)</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8">
            {/* Colorful bar */}
            <div className="h-1 flex mb-6 rounded-full overflow-hidden">
              <div className="flex-1 bg-[#00bcd4]" />
              <div className="flex-1 bg-[#c850c0]" />
              <div className="flex-1 bg-[#4caf50]" />
              <div className="flex-1 bg-[#ffeb3b]" />
              <div className="flex-1 bg-[#ff6b6b]" />
              <div className="flex-1 bg-[#2196f3]" />
            </div>
            <p className="text-center text-white/50 text-sm">© 2026 Harbor Point Holdings d/b/a The Village. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
